// src/pages/PublisherManagedAuthorUploadPage.jsx
// Simple Ingestion upload flow for Publisher Managed Authors
// Uses StreamlinedUploadFlow component with publisher-specific API endpoints
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PublisherNav from '../components/PublisherNav';
import StreamlinedUploadFlow from '../components/StreamlinedUploadFlow';
import { ArrowLeft, User, Upload } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { BACKEND_URL } from '@/config/api';
import { toast } from 'sonner';

const API = `${BACKEND_URL}/api`;

const PublisherManagedAuthorUploadPage = () => {
  const { authorId } = useParams();
  const navigate = useNavigate();
  
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadFlow, setShowUploadFlow] = useState(true);

  useEffect(() => {
    fetchAuthor();
  }, [authorId]);

  const fetchAuthor = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API}/publisher/authors/${authorId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuthor(data);
      } else {
        toast.error('Author not found');
        navigate('/publisher/authors');
      }
    } catch (error) {
      console.error('Error fetching author:', error);
      toast.error('Failed to load author');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClose = () => {
    navigate(`/publisher/authors/${authorId}/console`);
  };

  const handleUploadComplete = (data) => {
    console.log('[PublisherUpload] Upload complete:', data);
    toast.success('Manuscript uploaded successfully!');
    // Navigate to titles page for this managed author
    navigate(`/publisher/authors/${authorId}/titles`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50">
        <PublisherNav />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-indigo-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <PublisherNav />
      
      <main className="mx-auto max-w-4xl px-4 py-8">
        {/* Back Navigation */}
        <Link
          to={`/publisher/authors/${authorId}/console`}
          className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {author?.pen_name || 'Author'}&apos;s Console
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="rounded-full bg-indigo-600/20 p-3">
              <Upload className="w-8 h-8 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Upload New Manuscript</h1>
              <p className="text-slate-400">
                Uploading for <strong className="text-slate-200">{author?.pen_name}</strong>
              </p>
            </div>
          </div>
          
          {/* Author Card */}
          <div className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
            {author?.headshot_image_url ? (
              <img
                src={`${BACKEND_URL}${author.headshot_image_url}`}
                alt={author?.pen_name}
                className="w-12 h-12 rounded-lg object-cover border border-slate-600"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-slate-700 border border-slate-600 flex items-center justify-center">
                <User className="w-6 h-6 text-slate-500" />
              </div>
            )}
            <div>
              <p className="font-medium text-white">{author?.pen_name}</p>
              <p className="text-sm text-slate-400">Managed Author</p>
            </div>
          </div>
        </div>

        {/* Upload Flow Component */}
        <StreamlinedUploadFlow
          isOpen={showUploadFlow}
          onClose={handleUploadClose}
          onComplete={handleUploadComplete}
          // Publisher-specific configuration
          apiBaseUrl={`${API}/publisher/managed-authors/${authorId}/simple-ingest`}
          publisherAuthorId={authorId}
          publisherAuthorName={author?.pen_name}
          isPublisherManagedAuthor={true}
        />
      </main>
      
      <UniversalFooter />
    </div>
  );
};

export default PublisherManagedAuthorUploadPage;
